import type { ChatTools, StaticToolName } from '@/components/chat/types/tool-types';
import type {
  DynamicToolUIPart,
  FileUIPart,
  UIMessage as Message,
  SourceUrlUIPart,
  StepStartUIPart,
  ToolUIPart,
} from 'ai';

export type MessagePart = Message['parts'][number];
export type AddPrCommentToolPart = Extract<ToolUIPart<ChatTools>, { type: 'tool-addPrComment' }>;
export type FocusCodeToolPart = Extract<ToolUIPart<ChatTools>, { type: 'tool-focusCode' }>;
export type SetPrFileViewedToolPart = Extract<
  ToolUIPart<ChatTools>,
  { type: 'tool-setPrFileViewed' }
>;
export type ProposeCustomAgentChangeToolPart = Extract<
  ToolUIPart<ChatTools>,
  { type: 'tool-proposeCustomAgentChange' }
>;

export type GenericStaticToolPart = {
  type: `tool-${string}`;
  state?: string;
  toolCallId?: string;
  output?: unknown;
  errorText?: string;
};

export type GenericToolSummaryPart = {
  state?: string;
  output?: unknown;
  errorText?: string;
  identifier: string;
};

/** Concatenated text parts of a message (tool/reasoning parts excluded). */
export function getMessageTextContent(message: Pick<Message, 'parts'>): string {
  return (message.parts ?? [])
    .filter((part): part is Extract<MessagePart, { type: 'text' }> => part.type === 'text')
    .map((part) => part.text)
    .join('\n');
}

export function isToolPart<TName extends StaticToolName>(
  part: MessagePart,
  name: TName,
): part is Extract<ToolUIPart<ChatTools>, { type: `tool-${TName}` }> {
  return part.type === `tool-${name}`;
}

export function isDynamicToolPart(part: MessagePart): part is DynamicToolUIPart {
  return part.type === 'dynamic-tool';
}

export function isExaDynamicToolPart(part: MessagePart): boolean {
  return isDynamicToolPart(part) && part.toolName === 'get_code_context_exa';
}

export function isAddPrCommentPart(part: MessagePart): part is AddPrCommentToolPart {
  return isToolPart(part, 'addPrComment');
}

export function isFocusCodePart(part: MessagePart): part is FocusCodeToolPart {
  return isToolPart(part, 'focusCode');
}

export function isSetPrFileViewedPart(part: MessagePart): part is SetPrFileViewedToolPart {
  return isToolPart(part, 'setPrFileViewed');
}

export function isProposeCustomAgentChangePart(
  part: MessagePart,
): part is ProposeCustomAgentChangeToolPart {
  return isToolPart(part, 'proposeCustomAgentChange');
}

export function isInteractiveProposalToolPart(part: MessagePart): boolean {
  return isProposeCustomAgentChangePart(part);
}

export function isGenericStaticToolPart(
  part: MessagePart,
): part is MessagePart & GenericStaticToolPart {
  return part.type.startsWith('tool-');
}

export function isStepStartPart(part: MessagePart): part is StepStartUIPart {
  return part.type === 'step-start';
}

export function isSourceUrlPart(part: MessagePart): part is SourceUrlUIPart {
  return part.type === 'source-url';
}

export function isFilePart(part: MessagePart): part is FileUIPart {
  return part.type === 'file';
}

export function humanizeToolName(toolType: string): string {
  return toolType
    .replace(/^tool-/, '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

const TOOL_DISPLAY_NAMES: Record<string, string> = {
  readPrSummary: 'PR summary',
  listChangedFiles: 'Changed files',
  readChangedFile: 'Changed file',
  searchPrArtifacts: 'PR artifact search',
  readRepoMemory: 'Repo memory',
  listThreads: 'Review threads',
  readThread: 'Review thread',
  readChecksSummary: 'Checks summary',
  setPrFileViewed: 'Viewed state',
  listCustomAgents: 'Custom agents',
  proposeCustomAgentChange: 'Custom agent proposal',
};

const PR_CONTEXT_TOOL_NAMES = new Set([
  'readPrSummary',
  'listChangedFiles',
  'readChangedFile',
  'searchPrArtifacts',
  'readRepoMemory',
  'listThreads',
  'readThread',
  'readChecksSummary',
]);

function normalizeToolName(toolType: string): string {
  return toolType.replace(/^tool-/, '');
}

export function getToolDisplayName(toolType: string): string {
  const normalizedToolName = normalizeToolName(toolType);
  return TOOL_DISPLAY_NAMES[normalizedToolName] ?? humanizeToolName(toolType);
}

export function getToolTraceName(toolType: string): string {
  const normalizedToolName = normalizeToolName(toolType);
  return PR_CONTEXT_TOOL_NAMES.has(normalizedToolName)
    ? 'PR context'
    : getToolDisplayName(toolType);
}

// Wrap raw text in a markdown code fence whose backtick run is long enough to
// safely contain any backtick runs inside the content. Without this, a stdout
// blob that itself contains ``` would break out of the fence.
export function fenceText(content: string, language: string): string {
  const longestBacktickRun = (content.match(/`+/g) ?? []).reduce(
    (max, run) => Math.max(max, run.length),
    0,
  );
  const fence = '`'.repeat(Math.max(3, longestBacktickRun + 1));
  return `${fence}${language}\n${content}\n${fence}`;
}

// Cheap heuristic: detect unified-diff-shaped output so we can give Shiki a
// useful language hint. Mirrors `inferCodeFenceLanguage` in markdown.tsx; kept
// local to avoid pulling the React markdown module into this server-safe utils file.
function inferShellOutputLanguage(text: string): 'diff' | 'text' {
  const lines = text.split('\n');
  const hasGitDiffHeader = lines.some((line) => line.startsWith('diff --git '));
  const hasUnifiedHunk = lines.some((line) => line.startsWith('@@ ') && line.includes(' @@', 3));
  const hasFileHeaders =
    lines.some((line) => line.startsWith('--- ')) && lines.some((line) => line.startsWith('+++ '));
  return hasGitDiffHeader || hasUnifiedHunk || hasFileHeaders ? 'diff' : 'text';
}

export function extractGenericToolSummary(part: GenericToolSummaryPart): string | null {
  if (part.state === 'output-error') {
    return part.errorText ?? 'The action failed.';
  }

  if (!part.output) {
    return null;
  }

  if (typeof part.output === 'string') {
    return part.output;
  }

  if (typeof part.output === 'object') {
    const output = part.output as Record<string, unknown>;
    const stdout = typeof output.stdout === 'string' ? output.stdout.trim() : '';
    const stderr = typeof output.stderr === 'string' ? output.stderr.trim() : '';
    const failedExitCode = typeof output.exitCode === 'number' && output.exitCode > 0;

    if (typeof output.summary === 'string' && output.summary.trim().length > 0) {
      return output.summary;
    }

    if (typeof output.context === 'string' && output.context.trim().length > 0) {
      return output.context;
    }

    if (typeof output.content === 'string' && output.content.trim().length > 0) {
      return output.content;
    }

    // Always wrap raw shell output in a fenced code block before handing it to
    // the markdown renderer. Otherwise a `git diff` (lines starting with `-`,
    // 4-space-indented context, etc.) gets parsed as bullet lists + indented
    // code blocks instead of being shown verbatim. Mirrors how aider, cline,
    // continue, openai/codex, and claude-code render shell/diff output.
    if (failedExitCode && stdout && stderr) {
      return `**STDOUT:**\n${fenceText(stdout, inferShellOutputLanguage(stdout))}\n\n**STDERR:**\n${fenceText(stderr, 'text')}`;
    }

    if (failedExitCode && stderr) {
      return fenceText(stderr, 'text');
    }

    if (stdout) {
      return fenceText(stdout, inferShellOutputLanguage(stdout));
    }

    if (stderr) {
      return fenceText(stderr, 'text');
    }

    if (typeof output.exitCode === 'number' && output.exitCode === 0) {
      return 'Command completed (no output).';
    }

    if (Object.keys(output).length === 0) {
      return part.identifier === 'tool-think' ? null : 'Action completed.';
    }

    try {
      return `\`\`\`json\n${JSON.stringify(output, null, 2)}\n\`\`\``;
    } catch {
      return 'Action completed.';
    }
  }

  return null;
}

export function getGenericToolLoadingMessage(toolType: string): string {
  const normalizedToolType = toolType.replace(/^tool-/, '');

  if (normalizedToolType === 'bash') {
    return 'Running command...';
  }
  if (normalizedToolType === 'think') {
    return 'Thinking...';
  }

  return 'Working...';
}

export function isToolOutputComplete(state?: string): boolean {
  return state === 'output-available' || state === 'output-error';
}

export const TOOL_MARKDOWN_TEXT_CLASS =
  '[&_h1]:text-xs [&_h2]:text-xs [&_h3]:text-xs [&_h4]:text-xs [&_h5]:text-xs [&_h6]:text-xs [&_li]:text-xs [&_ol]:text-xs [&_p]:text-xs [&_table]:text-xs [&_td]:text-xs [&_th]:text-xs [&_ul]:text-xs';

export function getSafeExternalHref(rawUrl: string): string | null {
  try {
    const parsed = new URL(rawUrl);

    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return rawUrl;
    }

    return null;
  } catch {
    return null;
  }
}

export function isRenderedDisclosureBlockPart(part: MessagePart | undefined): boolean {
  if (!part) {
    return false;
  }

  if (part.type === 'reasoning') {
    return true;
  }

  if (isDynamicToolPart(part)) {
    return true;
  }

  return part.type.startsWith('tool-');
}

export function getPreviousRenderablePart(
  parts: MessagePart[],
  currentIndex: number,
): MessagePart | undefined {
  for (let index = currentIndex - 1; index >= 0; index -= 1) {
    const part = parts[index];

    if (!part) {
      continue;
    }

    // Step markers split model turns, but disclosure rows should still stack tightly across them.
    if (isStepStartPart(part)) {
      continue;
    }

    if (part.type === 'text') {
      if (part.text.trim().length === 0) {
        continue;
      }

      return part;
    }

    if (part.type === 'reasoning' || isSourceUrlPart(part) || isFilePart(part)) {
      return part;
    }

    if (isDynamicToolPart(part)) {
      return part;
    }

    if (part.type.startsWith('tool-')) {
      return part;
    }
  }

  return undefined;
}

export function getNextRenderablePart(
  parts: MessagePart[],
  currentIndex: number,
): MessagePart | undefined {
  for (let index = currentIndex + 1; index < parts.length; index += 1) {
    const part = parts[index];

    if (!part) {
      continue;
    }

    // Step markers split model turns, but disclosure rows should still stack tightly across them.
    if (isStepStartPart(part)) {
      continue;
    }

    if (part.type === 'text') {
      if (part.text.trim().length === 0) {
        continue;
      }

      return part;
    }

    if (part.type === 'reasoning' || isSourceUrlPart(part) || isFilePart(part)) {
      return part;
    }

    if (isDynamicToolPart(part)) {
      return part;
    }

    if (part.type.startsWith('tool-')) {
      return part;
    }
  }

  return undefined;
}

export function getToolDisclosureKey(part: MessagePart, index: number): string | null {
  if (isDynamicToolPart(part)) {
    return `dynamic:${part.toolName}:${part.toolCallId ?? index}`;
  }

  if (part.type.startsWith('tool-')) {
    return `static:${part.type}:${'toolCallId' in part ? (part.toolCallId ?? index) : index}`;
  }

  return null;
}

export function getMessagePartSignature(part: MessagePart, index: number): string {
  if (part.type === 'text') {
    return `text:${index}:${part.text}`;
  }

  if (part.type === 'reasoning') {
    return `reasoning:${index}:${part.state}:${part.text}`;
  }

  if (part.type === 'source-url') {
    return `source:${index}:${part.url}:${part.title ?? ''}`;
  }

  if (part.type === 'file') {
    return `file:${index}:${part.filename ?? ''}:${part.mediaType ?? ''}:${part.url}`;
  }

  if (part.type === 'step-start') {
    return `step-start:${index}`;
  }

  try {
    return `${part.type}:${index}:${JSON.stringify(part)}`;
  } catch {
    return `${part.type}:${index}:${'toolCallId' in part ? (part.toolCallId ?? 'unknown') : 'unknown'}`;
  }
}

export function getPersistentPartKey(messageId: string, part: MessagePart, index: number): string {
  if (isDynamicToolPart(part)) {
    return `dynamic:${messageId}:${part.toolName}:${part.toolCallId ?? index}`;
  }

  if (part.type.startsWith('tool-')) {
    return `tool:${messageId}:${part.type}:${'toolCallId' in part ? (part.toolCallId ?? index) : index}`;
  }

  if (isSourceUrlPart(part)) {
    return `source:${messageId}:${part.sourceId ?? part.url}:${index}`;
  }

  if (part.type === 'file') {
    return `file:${messageId}:${part.url}:${part.filename ?? ''}:${part.mediaType ?? ''}:${index}`;
  }

  return `${messageId}:${part.type}:${index}`;
}
